import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { systemLabel } from '@/lib/systems';
import { Users, Wrench, FileText, Activity, Database, Trash2, UserPlus, X, Plus, BookOpen, Sparkles, BarChart3, Download, Upload as UploadIcon, Save } from 'lucide-react';

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="surface p-5">
      <div className="flex justify-between">
        <span className="overline text-gray-500">{label}</span>
        <Icon className="w-4 h-4 text-gray-400" />
      </div>
      <div className="font-display text-3xl font-black mt-2 tracking-tight">{value}</div>
    </div>
  );
}

const EMPTY_TERM = { term_it: '', term_es: '', term_en: '', category: '', notes: '' };

export default function AdminPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'it';
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [glossary, setGlossary] = useState([]);
  const [tab, setTab] = useState('users');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', password: '', role: 'tecnico', language: 'it' });
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [glossaryForm, setGlossaryForm] = useState(EMPTY_TERM);

  const loadAll = () => {
    api.get('/admin/stats').then((r) => setStats(r.data));
    api.get('/users').then((r) => setUsers(r.data));
    api.get('/machines').then((r) => setMachines(r.data));
    api.get('/documents').then((r) => setDocuments(r.data));
    api.get('/interventions').then((r) => setInterventions(r.data));
    api.get('/glossary').then((r) => setGlossary(r.data));
  };
  useEffect(() => { loadAll(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/register', form);
      toast.success(t('app.create'));
      setOpen(false);
      setForm({ email: '', full_name: '', password: '', role: 'tecnico', language: 'it' });
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const submitGlossary = async (e) => {
    e.preventDefault();
    try {
      await api.post('/glossary', glossaryForm);
      toast.success(t('app.create'));
      setGlossaryOpen(false);
      setGlossaryForm(EMPTY_TERM);
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const delGlossary = async (id) => {
    if (!window.confirm(t('admin.glossary.delete_confirm'))) return;
    await api.delete(`/glossary/${id}`); toast.success(t('app.delete')); loadAll();
  };

  const delUser = async (id) => {
    if (!window.confirm(t('app.confirm') + '?')) return;
    await api.delete(`/users/${id}`); toast.success(t('app.delete')); loadAll();
  };
  const delMachine = async (id) => {
    if (!window.confirm(t('machines.delete_confirm'))) return;
    await api.delete(`/machines/${id}`); toast.success(t('app.delete')); loadAll();
  };
  const delDoc = async (id) => {
    if (!window.confirm(t('documents.delete_confirm'))) return;
    await api.delete(`/documents/${id}`); toast.success(t('app.delete')); loadAll();
  };

  const TABS = [
    { key: 'users', label: t('admin.users'), count: users.length },
    { key: 'machines', label: t('admin.machines'), count: machines.length },
    { key: 'documents', label: t('admin.documents'), count: documents.length },
    { key: 'interventions', label: t('admin.interventions'), count: interventions.length },
    { key: 'glossary', label: t('admin.manage_glossary'), count: glossary.length },
    { key: 'backup', label: 'Backup & Restore', count: null },
  ];

  // Backup & restore handlers
  const importRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const exportSnapshot = async () => {
    try {
      const r = await api.get('/admin/export-snapshot', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[T:]/g, '_');
      a.download = `ev_snapshot_${ts}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Snapshot scaricato');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Export fallito'); }
  };

  const importSnapshot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('overwrite', String(overwrite));
      const r = await api.post('/admin/import-snapshot', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(r.data);
      toast.success(`Import completato: ${r.data.total} record`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import fallito');
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = '';
    }
  };

  return (
    <div className="animate-fade-up" data-testid="admin-page">
      <div className="overline text-[#002FA7]">ADMIN</div>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-2">{t('admin.title')}</h1>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6">
          <Stat label={t('admin.users')} value={stats.users} icon={Users} />
          <Stat label={t('admin.machines')} value={stats.machines} icon={Wrench} />
          <Stat label={t('admin.documents')} value={stats.documents} icon={FileText} />
          <Stat label={t('admin.interventions')} value={stats.interventions} icon={Activity} />
          <Stat label={t('admin.chunks')} value={stats.chunks} icon={Database} />
        </div>
      )}

      {stats?.by_system && stats.by_system.length > 0 && (
        <div className="surface p-5 mt-4" data-testid="admin-stats-by-system">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-[#002FA7]" />
            <span className="overline text-gray-500">FREQUENZA PER SISTEMA</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {stats.by_system.map((row) => {
              const max = stats.by_system[0]?.total || 1;
              const pct = Math.round((row.total / max) * 100);
              return (
                <div key={row.system} className="border border-gray-200 p-3" data-testid={`sys-row-${row.system}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-display font-bold capitalize">{systemLabel(row.system, lang)}</div>
                    <span className="font-mono text-sm">{row.total}</span>
                  </div>
                  <div className="mt-2 bg-gray-100 h-1.5 overflow-hidden">
                    <div className="h-full bg-[#002FA7]" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="mt-2 text-xs text-gray-500 font-mono flex gap-3">
                    <span><Sparkles className="w-3 h-3 inline mr-1" />{row.patterns} pattern</span>
                    <span><BookOpen className="w-3 h-3 inline mr-1" />{row.field_reports} report</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-8 border-b border-gray-200 flex gap-0 overflow-x-auto" data-testid="admin-tabs">
        {TABS.map((tb) => (
          <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`tab-${tb.key}`}
            className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${
              tab === tb.key ? 'border-[#002FA7] text-[#002FA7] font-semibold' : 'border-transparent text-gray-500 hover:text-gray-900'
            }`}>
            {tb.label} {tb.count !== null && <span className="ml-1 font-mono text-xs">({tb.count})</span>}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === 'users' && (
          <div>
            <div className="flex justify-end mb-3">
              <button onClick={() => setOpen(true)} className="btn-primary" data-testid="create-user-btn"><UserPlus className="w-4 h-4" /> {t('app.create')}</button>
            </div>
            <div className="surface divide-y divide-gray-200">
              {users.map((u) => (
                <div key={u.id} className="p-4 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold">{u.full_name}</div>
                    <div className="text-xs text-gray-500 font-mono">{u.email} · {u.language || 'it'}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`overline px-2 py-1 ${
                      u.role === 'admin' ? 'bg-red-100 text-red-800' : u.role === 'tecnico' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'
                    }`}>{u.role}</span>
                    <button onClick={() => delUser(u.id)} className="btn-ghost text-red-600" data-testid={`del-user-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === 'machines' && (
          <div className="surface divide-y divide-gray-200">
            {machines.map((m) => (
              <div key={m.id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{m.brand} {m.model}</div>
                  <div className="text-xs text-gray-500 font-mono">{m.serial_number} · {m.year} · {m.client_name}</div>
                </div>
                <button onClick={() => delMachine(m.id)} className="btn-ghost text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
        {tab === 'documents' && (
          <div className="surface divide-y divide-gray-200">
            {documents.map((d) => (
              <div key={d.id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold truncate">{d.filename}</div>
                  <div className="text-xs text-gray-500 font-mono">{d.brand}/{d.model} · {d.doc_type} · {d.pages}p</div>
                </div>
                <button onClick={() => delDoc(d.id)} className="btn-ghost text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
        {tab === 'interventions' && (
          <div className="surface divide-y divide-gray-200">
            {interventions.map((i) => (
              <div key={i.id} className="p-4">
                <div className="font-semibold">{i.machine_label}</div>
                <div className="text-xs text-gray-500">{i.technician_name} · {new Date(i.created_at).toLocaleString()}</div>
                <div className="text-sm mt-1 text-gray-700">{i.symptom} → {i.solution}</div>
              </div>
            ))}
          </div>
        )}
        {tab === 'glossary' && (
          <div>
            <div className="flex justify-between items-center mb-3">
              <div className="text-sm text-gray-500">{t('admin.glossary.subtitle')}</div>
              <button onClick={() => setGlossaryOpen(true)} className="btn-primary" data-testid="add-glossary-btn">
                <Plus className="w-4 h-4" /> {t('admin.glossary.add')}
              </button>
            </div>
            <div className="surface divide-y divide-gray-200">
              {glossary.length === 0 ? (
                <div className="p-12 text-center">
                  <BookOpen className="w-10 h-10 text-gray-300 mx-auto" strokeWidth={1.2} />
                  <div className="mt-3 font-display font-bold">{t('admin.glossary.no_terms')}</div>
                </div>
              ) : glossary.map((g) => (
                <div key={g.id} className="p-4 flex items-start justify-between gap-3" data-testid={`gloss-${g.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div><span className="overline text-gray-400">IT</span><div className="font-semibold">{g.term_it}</div></div>
                      <div><span className="overline text-gray-400">ES</span><div className="font-semibold">{g.term_es}</div></div>
                      <div><span className="overline text-gray-400">EN</span><div className="font-semibold">{g.term_en}</div></div>
                    </div>
                    {(g.category || g.notes) && (
                      <div className="text-xs text-gray-500 mt-2 font-mono">{g.category} {g.notes && `· ${g.notes}`}</div>
                    )}
                  </div>
                  <button onClick={() => delGlossary(g.id)} className="btn-ghost text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === 'backup' && (
          <div className="space-y-6" data-testid="backup-tab">
            <section className="surface p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center">
                  <Download className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="font-display font-bold text-lg">Esporta snapshot</div>
                  <div className="text-sm text-gray-500">Scarica un file JSON con tutti i dati di business (macchine, documenti, chunks indicizzati, interventi, field reports, procedure patterns, glossario). Gli utenti e la chat history non sono inclusi.</div>
                </div>
              </div>
              <button onClick={exportSnapshot} className="btn-primary" data-testid="export-snapshot-btn">
                <Download className="w-4 h-4" /> Scarica snapshot JSON
              </button>
            </section>

            <section className="surface p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-50 text-amber-700 flex items-center justify-center">
                  <UploadIcon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="font-display font-bold text-lg">Importa snapshot</div>
                  <div className="text-sm text-gray-500">Carica un JSON esportato da un'altra istanza per ripopolare questo database. I record con ID già esistenti vengono saltati a meno che tu non abiliti "sovrascrivi".</div>
                </div>
              </div>
              <label className="inline-flex items-center gap-2 mb-3 text-sm">
                <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="import-overwrite" />
                <span>Sovrascrivi record esistenti (stesso ID)</span>
              </label>
              <input ref={importRef} type="file" accept="application/json,.json"
                onChange={importSnapshot} disabled={importing}
                className="block w-full text-sm border border-gray-300 p-2"
                data-testid="import-snapshot-input" />
              {importing && <div className="mt-3 text-sm text-blue-700">Importazione in corso…</div>}
              {importResult && (
                <div className="mt-4 border border-green-300 bg-green-50 p-3 text-sm">
                  <div className="font-semibold text-green-800 mb-2">Import completato — {importResult.total} record applicati</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 font-mono text-xs">
                    {Object.keys(importResult.inserted).map((k) => (
                      <div key={k} className="border border-green-200 bg-white p-2">
                        <div className="font-semibold uppercase text-gray-700">{k}</div>
                        <div>+ {importResult.inserted[k]} nuovi</div>
                        {importResult.overwritten[k] > 0 && <div>↻ {importResult.overwritten[k]} sovrascritti</div>}
                        {importResult.skipped[k] > 0 && <div className="text-gray-500">- {importResult.skipped[k]} saltati</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <div className="bg-blue-50 border border-blue-200 p-3 text-sm flex gap-2 items-start">
              <Save className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" />
              <div className="text-gray-700">
                <strong>Migrazione produzione:</strong> esporta dall'anteprima, deploya, login come admin sul deploy, poi importa.
                I documenti PDF originali non sono inclusi nel JSON ma i loro chunk indicizzati sì — la chat AI continua a funzionare.
              </div>
            </div>
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={createUser} className="bg-white w-full max-w-md border border-gray-200" data-testid="user-form">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="font-display font-bold">{t('app.create')}</div>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div><label className="overline block mb-1">{t('login.full_name')}</label><input required className="input-base" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
              <div><label className="overline block mb-1">{t('login.email')}</label><input type="email" required className="input-base" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="overline block mb-1">{t('login.password')}</label><input type="password" required minLength={6} className="input-base" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-1">{t('login.role')}</label>
                  <select className="input-base" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    <option value="tecnico">tecnico</option><option value="admin">admin</option><option value="cliente">cliente</option>
                  </select>
                </div>
                <div>
                  <label className="overline block mb-1">{t('app.language')}</label>
                  <select className="input-base" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
                    <option value="it">Italiano</option><option value="es">Español</option><option value="en">English</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>{t('app.cancel')}</button>
              <button type="submit" className="btn-primary" data-testid="user-submit">{t('app.create')}</button>
            </div>
          </form>
        </div>
      )}

      {glossaryOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setGlossaryOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitGlossary} className="bg-white w-full max-w-lg border border-gray-200" data-testid="glossary-form">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="font-display font-bold">{t('admin.glossary.add')}</div>
              <button type="button" onClick={() => setGlossaryOpen(false)} className="btn-ghost"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div><label className="overline block mb-1">{t('admin.glossary.term_it')}</label><input required className="input-base" value={glossaryForm.term_it} onChange={(e) => setGlossaryForm({ ...glossaryForm, term_it: e.target.value })} /></div>
              <div><label className="overline block mb-1">{t('admin.glossary.term_es')}</label><input required className="input-base" value={glossaryForm.term_es} onChange={(e) => setGlossaryForm({ ...glossaryForm, term_es: e.target.value })} /></div>
              <div><label className="overline block mb-1">{t('admin.glossary.term_en')}</label><input required className="input-base" value={glossaryForm.term_en} onChange={(e) => setGlossaryForm({ ...glossaryForm, term_en: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline block mb-1">{t('admin.glossary.category')}</label><input className="input-base" value={glossaryForm.category} onChange={(e) => setGlossaryForm({ ...glossaryForm, category: e.target.value })} /></div>
                <div><label className="overline block mb-1">{t('admin.glossary.notes')}</label><input className="input-base" value={glossaryForm.notes} onChange={(e) => setGlossaryForm({ ...glossaryForm, notes: e.target.value })} /></div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={() => setGlossaryOpen(false)}>{t('app.cancel')}</button>
              <button type="submit" className="btn-primary" data-testid="glossary-submit">{t('app.create')}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
